import { Box, Button, Checkbox, IconButton, Tooltip } from "@mui/material";
import { Modal, TextField, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useContext, useEffect, useState } from "react";
import { BORDER_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";
import {
  GetEnterpriseAllUsersResponse,
  UpdateProjectResponse,
} from "../../proto/api_pb";
import { Collaborator, Project, User } from "../../proto/api_pb";
import { CustomPaper } from "../../pages/MyTracks/styles";
import { SelectField, useStyles } from "../../pages/MyTracks/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import {
  AuthContext,
  AuthContextInterface,
  S3CredentialsContext,
  S3CredentialsContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { addEnterpriseMemberModalStyles, addItemsModalStyles } from "./styles";
import { updateProject } from "../../services/projectService";
import { toast } from "react-toastify";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import CustomButton from "../CustomButton/CustomButton";
import { MoodsArray } from "../constants";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface EditProjectModalProps {
  project: Project;
  handleUseEffect: () => Promise<void>;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  project,
  handleUseEffect,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedCollaborators, setSelectedCollaborators] =
    useState<string[]>();
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [photo, setPhoto] = useState<string | null>(null);
  const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const [projectName, setProjectName] = useState<string>(project.getName());
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const classes = useStyles();
  const { enterprise } = useContext(UserDetailsContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [moods, setMoods] = useState<string[]>(project.getMoodsList() || []);
  
  const handleOpen = () => {
    setPhoto(null);
    setFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    if (project) {
      const u = project
        .getCollaboratorsList()
        .map((user) => user.getUsername());
      setSelectedCollaborators(u);
      setProjectName(project.getName());
      setPhoto(project.getArtworkUrl());
      setMoods(project.getMoodsList() || []);
      setFile(null);
      setPhoto(null);
    }
    setFile(null);
    setOpen(false);
  };

  const filteredUserNames = enterpriseUsers
    .filter((user) => user.getUserId() !== userId)
    .map((user) => user.getUsername());

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        setEnterpriseUsers(
          res
            .getUsersList()
            .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
        );
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken]);

  const uploadArtworkImage = async () => {
    let profilePictureString = "";
    if (file) {
      const key = await awaitableUploadToS3(
        userId,
        file!,
        `project-art`,
        file!.type,
        awsAccessKeyId!,
        awsSecretAccessKey!,
        awsSessionToken!,
        () => {}
      );
      profilePictureString = ASSETS_URL + "/" + key;
    }
    return profilePictureString;
  };

  useEffect(() => {
    if (project) {
      const u = project
        .getCollaboratorsList()
        .map((user) => user.getUsername());
      setSelectedCollaborators(u);
      setProjectName(project.getName());
      setMoods(project.getMoodsList() || []);
    }
  }, [project]);

  const handleChangeCollaboratorsList = async (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }
    setSelectedCollaborators(tempArr);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFile = Array.from(files).find((file) =>
        file.type.startsWith("image/")
      );
      if (imageFile) {
        const safeFileName = imageFile.name
          .normalize("NFKD")
          .replace(/[^\u0020-\u007E]/g, "");
        const safeFile = new File([imageFile], safeFileName, {
          type: imageFile.type,
        });
        setFile(safeFile);
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            setPhoto(reader.result as string);
          }
        };
        reader.readAsDataURL(safeFile);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const imageFile = Array.from(files).find((file) =>
        file.type.startsWith("image/")
      );
      if (imageFile) {
        const safeFileName = imageFile.name
          .normalize("NFKD")
          .replace(/[^\u0020-\u007E]/g, "");
        const safeFile = new File([imageFile], safeFileName, {
          type: imageFile.type,
        });
        setFile(safeFile);
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            setPhoto(reader.result as string);
          }
        };
        reader.readAsDataURL(safeFile);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async () => {
    if (!projectName.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }

    setLoading(true);
    const token = await getValidAccessToken();
    const p = project;
    p.setName(projectName);

    const collaborators: Collaborator[] = [];
    if (selectedCollaborators) {
      const selectedUsers = enterpriseUsers.filter((user) =>
        selectedCollaborators.includes(user.getUsername())
      );
      for (const user of selectedUsers) {
        const collaborator = new Collaborator();
        collaborator.setUserId(user.getUserId());
        collaborator.setUsername(user.getUsername());
        collaborator.setProfilePicture(user.getProfilePicture());
        collaborators.push(collaborator);
      }
    }
    p.setCollaboratorsList(collaborators);
    p.setMoodsList(moods);

    if (file) {
      try {
        const uploadedImageUrl = await uploadArtworkImage();
        p.setArtworkUrl(uploadedImageUrl);
        setPhoto(uploadedImageUrl);
      } catch (error) {
        toast.error("Image upload failed");
        setLoading(false);
        return;
      }
    }

    const res = await updateProject(token, p);
    if (res instanceof UpdateProjectResponse) {
      toast.info("Updated successfully");
      await handleUseEffect();
      handleClose();
    } else {
      console.log(res);
      handleClose();
    }
    setLoading(false);
  };

  return (
    <>
      <Tooltip title="Edit Project Details">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }}
        >
          <EditIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </Tooltip>
      <Modal
        onClick={(e) => e.stopPropagation()}
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={addEnterpriseMemberModalStyles.projectCollaboratorContainer}
        >
          <Typography
            sx={{ color: TEXT_COLOR }}
            id="modal-modal-title"
            variant="h6"
            component="h2"
          >
            Edit Project Details
          </Typography>
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Project Name <span className="asterisk">*</span>
          </Typography>
          <TextField
            fullWidth
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            sx={{ marginBottom: "15px" }}
          />
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Select Collaborators
          </Typography>
          <SelectField
            value={selectedCollaborators}
            multiple
            fullWidth
            id="collaborator-tags"
            options={filteredUserNames}
            disableCloseOnSelect
            onChange={handleChangeCollaboratorsList}
            freeSolo
            ListboxProps={{ style: { maxHeight: 150 } }}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                <>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={""}
                InputLabelProps={{ shrink: false }}
                classes={{ root: classes.customTextField }}
              />
            )}
            PaperComponent={CustomPaper}
          />
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Project Mood
          </Typography>
          <SelectField
            value={moods}
            fullWidth
            multiple
            id="mood-select"
            options={MoodsArray}
            onChange={(_event, value) => setMoods(value as string[])}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select or type moods"
                InputLabelProps={{ shrink: false }}
                classes={{ root: classes.customTextField }}
              />
            )}
            PaperComponent={CustomPaper}
          />
          <Box sx={{ marginTop: "15px" }}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                ...addItemsModalStyles.uploadingBox,
                ...{
                  height: "200px",
                  border: `2px dashed ${BORDER_COLOR}`,
                },
              }}
            >
              <>
                <CloudUploadIcon sx={addItemsModalStyles.cloudUploadIcon} />
                <Typography variant="body1" sx={addItemsModalStyles.typography}>
                  Drag & Drop Artwork here or click the button below to upload.
                </Typography>
              </>
              <>
                <label htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={addItemsModalStyles.chooseMoreFilesButton}
                  >
                    Choose Artwork for Project
                  </Button>
                </label>
              </>
            </Box>
            {photo && (
              <div>
                <img
                  src={photo}
                  alt="Uploaded"
                  style={addItemsModalStyles.artWorkImg}
                />
              </div>
            )}
          </Box>
          <Box sx={addEnterpriseMemberModalStyles.buttonContainer}>
            <CustomButton
              text="Submit"
              onClick={handleSubmit}
              loading={loading}
              customStyles={{
                width: "200px",
              }}
            />
            <CustomButton
              text="Cancel"
              onClick={handleClose}
              isSkipButton={true}
              customStyles={{
                width: "200px",
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};